/// <amd-module name="VSS/Platform/FPS" />
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { ICompletedContent, IContentAttributes, IVssContentService } from "./Content";
import { FastPageSwitchEventDetail, IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { DataProviderResult } from "./Contracts/Contributions";
import { IVssRequestOptions, issueRequest } from "./Fetch";
import { HistoryServiceEventNames, INavigationStateChangedEvent, IVssHistoryService } from "./History";
import { IVssNavigationService } from "./Navigation";
import { IVssPageService } from "./Page";
import { IVssPerformanceService } from "./Performance";
import { publishEvent } from "./Telemetry";
import { ITokenProviderService } from "./TokenProvider";
import { deserializeVssJsonObject, updateJsonScriptValue } from "./Util/Serialization";
import { tryGetLocalStorageObject, trySetLocalStorageObject } from "./Util/Storage";
import { Uri } from "./Util/Url";

export interface IFPSData {
    /**
     * Source identifier for this command to use if reporting telemetry
     */
    telemetrySource?: string;

    /**
     * Additional properties to use when reporting telemetry when this command is clicked
     */
    telemetryProperties?: { [propertyName: string]: any };

    /**
     * Optional prop indicating the navigation element which we're about to FPS to.
     */
    navigationId?: string;

    /**
     * Additional properties that FPS events can leverage
     */
    [key: string]: any;
}

interface IFastPageSwitchResponse {
    fps: IFastPageSwitchData;
}

interface IFastPageSwitchData {
    content?: IContentAttributes[];
    dataProviders?: DataProviderResult;
    performance?: Object;
}
declare global {
    interface Performance {
        memory?: {
            /** The maximum size of the heap, in bytes, that is available to the context. */
            jsHeapSizeLimit: number;
            /** The total allocated heap size, in bytes. */
            totalJSHeapSize: number;
            /** The currently active segment of JS heap, in bytes. */
            usedJSHeapSize: number;
          };
    }
}

const localStorageFPSCIEventKey = "navigation/fpsCIEvent";

// Global that tracks the currently active fpsPromise.
let pagePromise: ICancelablePromise<Response> | null;

// Global that tracks the current FPS navigation id. When a history entry is popped,
// this allows us to know whether or not an FPS is necessary to get to the new state.
let fpsNavigationIdCounter = 0;

let lastUsedJSHeapSize = 0;

function createFPSTelemetryEventProperties(
    pageContext: IVssPageContext,
    source?: string,
    additionalProperties: { [propertyName: string]: any } = {}
): { [propertyName: string]: any } {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
    const displayedNavigation = navigationService.getDisplayedNavigation();

    const properties: { [propertyName: string]: any } = {
        ...additionalProperties,
        source: source || "unknown",
        sourceNavigationId: displayedNavigation[displayedNavigation.length - 1],
        sourceRouteId: navigationService.getCurrentRoute().routeId,
        startNavigationOffset: Date.now() - window.performance.timing["navigationStart"],
        serviceVersion: pageData ? pageData.serviceVersion : undefined
    };

    return properties;
}

function logFPSTelemetryEvent(pageContext: IVssPageContext, properties: { [propertyName: string]: any }): void {
    properties["endNavigationOffset"] = Date.now() - window.performance.timing["navigationStart"];

    publishEvent(pageContext, "WebPlatform", "FPS", properties);
}

/**
 * onClickFPS is a wrapper around FastPageSwitch that tracks the behaviors of clicking on links
 * or buttons that perform FPS navigation. This method can be called in response to an event to
 * perform the navigation. preventDefault is called on the event if an FPS is attempted.
 *
 * @param pageContext - The current pageContext
 * @param href - This is the URL that we are routing to
 * @param recordHistoryEntry - Should be set to true when a new history entry should be added for this navigation.
 * @param event - The event being handled. This method checks the event for specific control keys and buttons which indicate to skip the FPS. If provided and the FPS is executed, preventDefault is called on the event.
 * @param data - Optional FPS data
 */
export function onClickFPS(
    pageContext: IVssPageContext,
    href: string,
    recordHistoryEntry: boolean,
    event?: React.SyntheticEvent<HTMLElement>,
    data: IFPSData = {}
): boolean | void {
    const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;
    // Don't do any navigation processing on links when:
    //  ctrl/meta is used - open in new tab.
    //  shift is used - open in new window.
    //  middle mouse button is used - open in new tab.
    if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
        FastPageSwitch(pageContext, href, recordHistoryEntry, data);

        // Prevent the default navigation from taking place.
        if (event) {
            event.preventDefault();
        }

        return true;
    } else {
        logFPSTelemetryEvent(pageContext, {
            ...createFPSTelemetryEventProperties(pageContext, data.telemetrySource, data.telemetryProperties),
            external: true,
            ctrlKey,
            metaKey,
            shiftKey,
            middleClick: button === 1
        });
    }
}

/**
 * FastPageSwitch function is used to "navigate" the page to a new location through a contributed route.
 * This will perform the required updates to the document based on the changes to the data and UI.
 *
 * @param pageContext - The current pageContext.
 * @param href - This is the URL that we are routing too.
 * @param recordHistoryEntry - Should be set to true when a new history entry should be added for this navigation.
 */
export function FastPageSwitch(pageContext: IVssPageContext, href: string, recordHistoryEntry: boolean, data: IFPSData = {}): Promise<boolean> {
    const fpsNavigationId = ++fpsNavigationIdCounter;


    const fastPageSwitchEventDetail: FastPageSwitchEventDetail = {
        pageContext: pageContext,
        href: href,
        recordHistoryEntry: recordHistoryEntry,
        data: data,
        startTime: Date.now()
    };

    // If we have any active page switch pending, we need to cancel it before we start a new one.
    if (pagePromise) {
        pagePromise.cancel();
        pagePromise = null;
    }
    const fpsTelemetryProps = createFPSTelemetryEventProperties(pageContext, data.telemetrySource, data.telemetryProperties);

    if (performance?.memory?.usedJSHeapSize) {
        fpsTelemetryProps["usedJSHeapSize"] = performance.memory.usedJSHeapSize;
    }
    fpsTelemetryProps["navigationId"] = fpsNavigationId;

    const fpsEvent = new CustomEvent("fpsStarting", { cancelable: true, detail: { ...fastPageSwitchEventDetail } });

    if (performance?.memory) {
        // Check memory usage and bypass FPS if we see evidence of high memory usage.

        // performance.memory is deprecated and only available in Chromium based browsers. It is also unreliable
        // as it does not give a very accurate representation of the memory usage unless the browser is started with the --enable-precise-memory-info flag.
        // It is only used as a last resort here to prevent memory leaks and force a page refresh when memory usage gets too high.
        // This should force the browser to clean up memory and prevent out of memory errors in most extreme cases.
        // Worst case scenario is that every page navigation will result in a full page refresh.
        // See DTS: https://dev.azure.com/mseng/AzureDevOps/_workitems/edit/2107669
        const maxUsedJSHeapSize = 1073741824; // Cap memory usage at 1GB
        const currentUsedJSHeapSize = performance.memory.usedJSHeapSize;
        if (currentUsedJSHeapSize > maxUsedJSHeapSize && currentUsedJSHeapSize > lastUsedJSHeapSize) {
            fpsTelemetryProps["highMemoryUsage"] = true;
            trySetLocalStorageObject(localStorageFPSCIEventKey, fpsTelemetryProps);
            window.location.href = href;
            return Promise.resolve(false);
        }

        lastUsedJSHeapSize = currentUsedJSHeapSize;
    }

    // Dispatch the fpsStart event. If it is not canceled, we will complete the navigation.
    if (document.body.dispatchEvent(fpsEvent)) {
        // Form the request for a FastPageSwitch...
        const requestInit: RequestInit = {
            cache: "no-cache",
            method: "GET",
            mode: "same-origin"
        };

        // Add the FastPageSwitch request template parameter to the requested URL.
        const targetLocation = new Uri(href);
        targetLocation.addQueryParam("__rt", "fps");

        const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData) {
            targetLocation.addQueryParam("__ver", "" + pageData.webPlatformVersion);
        }

        // Now that all interested parties have had the opportunity to react in fpsStarting and have
        // not canceled the navigation, we will fire the fpsLoading event.
        document.body.dispatchEvent(new CustomEvent("fpsLoading", { cancelable: false, detail: { ...fastPageSwitchEventDetail } }));

        const performanceService = pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.addSplitTiming("fpsRequestStarting");

        // if we have a contributed token provider, use it
        let vssRequestOptions: IVssRequestOptions | undefined = undefined;

        const tokenService = pageContext.getService<ITokenProviderService>("ITokenProviderService");
        const tokenProvider = tokenService.getRedirectProvider(new Uri(href).absoluteUri);
        if (!!tokenProvider) {
            vssRequestOptions = {
                authTokenProvider: tokenProvider
            };
        }

        // Make the call to the server with a Fetch request and process the response.
        const localPromise = makeCancelable(issueRequest(targetLocation.absoluteUri, requestInit, vssRequestOptions));

        const completePromise = localPromise.promise
            .then((response: Response) => {
                // If the current promise we are completing is still the current page promise, clear it.
                if (pagePromise === localPromise) {
                    pagePromise = null;
                }

                // Read the response as Text, not as JSON since we need to apply our specialized deserialization.
                return response.text().then((bodyText: string) => {
                    const performanceService = pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                    performanceService.addSplitTiming("fpsResponseReceived");

                    let fpsResponse: IFastPageSwitchResponse | null = null;
                    try {
                        fpsResponse = deserializeVssJsonObject<IFastPageSwitchResponse>(bodyText);
                    } catch (ex) {
                        console.log(`Not a valid FPS response: ${ex.message || ex}. Performing full page navigation`);
                        fpsTelemetryProps["invalidFPSResponse"] = true;
                        trySetLocalStorageObject(localStorageFPSCIEventKey, fpsTelemetryProps);

                        window.location.href = href;
                        return true;
                    }

                    const navigationData = fpsResponse && fpsResponse.fps;

                    if (navigationData) {
                        // Given the return data from the service we will update the data in the document with the latest values.
                        if (navigationData.dataProviders) {
                            updateJsonScriptValue("dataProviders", navigationData.dataProviders, true);
                        }

                        if (navigationData.performance) {
                            updateJsonScriptValue("serverStatistics", navigationData.performance);
                        }

                        // Load the demanded scripts through the content service.
                        // NOTE: We don't wait for "synchronous" scripts to load like an initial page load would.
                        if (navigationData.content) {
                            const fpsService = pageContext.getService<IFastPageSwitchService>("IFastPageSwitchService");
                            if (fpsService.hasStaleContent(navigationData.content)) {
                                // We already have an older version loaded for one or more modules that we are attempting to load here.
                                // This will happen if the server has been updated since this page was initially loaded. Force a
                                // full page reload here to pick up consistent (new) content.
                                fpsTelemetryProps["staleContent"] = true;
                                logFPSTelemetryEvent(pageContext, fpsTelemetryProps);

                                window.location.href = href;
                                return true;
                            }

                            const contentService = pageContext.getService<IVssContentService>("IVssContentService");
                            contentService.resetResolvedContent();
                            contentService.loadScripts(navigationData.content);
                        }
                    }

                    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
                    if (recordHistoryEntry) {
                        historyService.pushState(undefined, href, undefined, { fpsNavigationId });
                    } else {
                        historyService.replaceState(undefined, href, undefined, { fpsNavigationId });
                    }

                    // Dispatch the loaded event to notify listeners that the page has been loaded.
                    document.body.dispatchEvent(
                        new CustomEvent("fpsLoaded", { cancelable: false, detail: { fpsResponse, ...fastPageSwitchEventDetail } })
                    );

                    // Dispatch the fpsCompleted event now that all listeners have had the chance to react to the loaded event.
                    document.body.dispatchEvent(
                        new CustomEvent("fpsCompleted", {
                            cancelable: false,
                            detail: { fpsResponse, success: true, ...fastPageSwitchEventDetail }
                        })
                    );

                    // Log telemetry
                    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
                    const displayedNavigation = navigationService.getDisplayedNavigation();
                    fpsTelemetryProps["targetNavigationId"] = displayedNavigation[displayedNavigation.length - 1];
                    fpsTelemetryProps["targetRouteId"] = navigationService.getCurrentRoute().routeId;
                    fpsTelemetryProps["success"] = true;
                    logFPSTelemetryEvent(pageContext, fpsTelemetryProps);

                    return true;
                });
            })
            .catch((reason: any) => {
                if (pagePromise === localPromise && !reason.isCanceled) {
                    // If we failed to transition as an FPS request we will just navigate to the page.
                    fpsTelemetryProps["failed"] = true;
                    trySetLocalStorageObject(localStorageFPSCIEventKey, fpsTelemetryProps);

                    window.location.href = href;
                } else {
                    fpsTelemetryProps["cancelledByOtherFPS"] = true;
                    logFPSTelemetryEvent(pageContext, fpsTelemetryProps);
                }

                // If the current promise we are completing is still the current page promise, clear it.
                if (pagePromise === localPromise) {
                    pagePromise = null;
                }

                document.body.dispatchEvent(
                    new CustomEvent("fpsCompleted", {
                        cancelable: false,
                        detail: { success: false, reason: reason, ...fastPageSwitchEventDetail }
                    })
                );
                return false;
            });

        // Save the current promise as the global promise for cancelation
        pagePromise = localPromise;

        // Return the underlying promise, not the cancelable promise.
        return completePromise;
    } else {
        fpsTelemetryProps["cancelledByUser"] = true;
        if (fpsEvent.detail.cancellationReason) {
            fpsTelemetryProps["cancellationReason"] = fpsEvent.detail.cancellationReason;
        }
        logFPSTelemetryEvent(pageContext, fpsTelemetryProps);
    }

    return Promise.resolve(false);
}

interface IFastPageSwitchService extends IVssService {
    /**
     * Returns true if, for any of the passed in content, we have already loaded a bundle with
     * the same contribution id but a different integrity value.
     */
    hasStaleContent(content: IContentAttributes[]): boolean;
}

class FastPageSwitchService extends VssService implements IFastPageSwitchService {
    private loadedContentIntegrity: { [key: string]: string } = {};
    private loadedContentFilenames: { [key: string]: string } = {};
    private bundlingEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        // Check to see if the page was loaded following a failed FPS by looking for
        // an object representing the FPS CI event in local storage. If it is there,
        // log the event.
        try {
            const ciEventData = tryGetLocalStorageObject<any>(localStorageFPSCIEventKey);
            trySetLocalStorageObject(localStorageFPSCIEventKey, undefined);

            if (ciEventData) {
                // Add target destination, which is the page we have just navigated to.
                const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
                const displayedNavigation = navigationService.getDisplayedNavigation();
                ciEventData["targetNavigationId"] = displayedNavigation[displayedNavigation.length - 1];
                ciEventData["targetRouteId"] = navigationService.getCurrentRoute().routeId;

                logFPSTelemetryEvent(pageContext, ciEventData);
            }
        } catch {
            // Error, probably JSON-parsing this event. Since it is just telemetry, ignore.
        }

        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.subscribe(this._onNavigate, HistoryServiceEventNames.statePopped);

        const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData && pageData.isBundlingEnabled) {
            this.bundlingEnabled = true;

            // With bundling enabled, track content loads for each webproj so that on FPS
            // we can issue a full page reload if the server content has changed since we last
            // loaded content for that bundle.
            const contentService = pageContext.getService<IVssContentService>("IVssContentService");
            contentService.subscribe(this._onContentLoaded, "contentComplete");
        }
    }

    public hasStaleContent(content: IContentAttributes[]): boolean {
        let staleContent = false;

        if (this.bundlingEnabled) {
            for (const bundle of content) {
                if (bundle.clientId) {
                    // Check whether the integrity OR url of the bundle is different from what we have (previously) loaded.
                    if (bundle.integrity) {
                        const existingContentIntegrity = this.loadedContentIntegrity[bundle.clientId];
                        if (existingContentIntegrity && existingContentIntegrity !== bundle.integrity) {
                            staleContent = true;
                            break;
                        }
                    }
                    const existingFilename = this.loadedContentFilenames[bundle.clientId];
                    if (existingFilename && existingFilename !== this.getFileName(bundle.url)) {
                        staleContent = true;
                        break;
                    }
                }
            }
        }

        return staleContent;
    }

    private getFileName(url: string): string {
        const lastSlash = url.lastIndexOf("/");
        return lastSlash >= 0 ? url.substr(lastSlash + 1) : url;
    }

    private _onNavigate = (stateChanged: INavigationStateChangedEvent) => {
        // Perform a FastPageSwitch to the navigation target.
        if (stateChanged.newState) {
            const oldNavId = stateChanged.oldState && stateChanged.oldState.data && stateChanged.oldState.data.fpsNavigationId;
            const newNavId = stateChanged.newState.data && stateChanged.newState.data.fpsNavigationId;

            if (oldNavId !== newNavId) {
                FastPageSwitch(this.pageContext, stateChanged.newState.url, false, {});
            }
        }
    };

    private _onContentLoaded = (completedContent: ICompletedContent): void => {
        const attr = completedContent.contentAttributes;
        if (attr && attr.clientId) {
            if (attr.integrity) {
                this.loadedContentIntegrity[attr.clientId] = attr.integrity;
            }
            this.loadedContentFilenames[attr.clientId] = this.getFileName(attr.url);
        }
    };
}

Services.add("IFastPageSwitchService", { serviceFactory: FastPageSwitchService, options: ServiceOptions.Persistant | ServiceOptions.Startup });
